import React, { useState, useEffect } from 'react';
import { menusAPI } from './api';

function MenuManagement() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    icon: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    try {
      const response = await menusAPI.getAll();
      const menuData = response.data.data || [];
      setMenus(menuData);
    } catch (error) {
      console.error('Failed to load menus:', error);
      alert('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMenu) {
        await menusAPI.update(editingMenu.id, formData);
        alert('Menu updated successfully!');
      } else {
        await menusAPI.create(formData);
        alert('Menu created successfully!');
      }
      setShowForm(false);
      setEditingMenu(null);
      setFormData({ title: '', icon: '', display_order: 0, is_active: true });
      loadMenus();
    } catch (error) {
      console.error('Failed to save menu:', error);
      alert('Failed to save menu');
    }
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    setFormData({
      title: menu.title,
      icon: menu.icon,
      display_order: menu.display_order,
      is_active: menu.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this menu?')) return;
    try {
      await menusAPI.delete(id);
      alert('Menu deleted successfully!');
      loadMenus();
    } catch (error) {
      console.error('Failed to delete menu:', error);
      alert('Failed to delete menu');
    }
  };

  const toggleActive = async (menu) => {
    try {
      await menusAPI.update(menu.id, { ...menu, is_active: !menu.is_active });
      loadMenus();
    } catch (error) {
      console.error('Failed to toggle menu status:', error);
      alert('Failed to toggle menu status');
    }
  };

  const moveUp = async (menu, index) => {
    if (index === 0) return;
    const prevMenu = menus[index - 1];
    
    try {
      await menusAPI.reorder([
        { id: menu.id, display_order: prevMenu.display_order },
        { id: prevMenu.id, display_order: menu.display_order }
      ]);
      await loadMenus();
    } catch (error) {
      console.error('Failed to reorder:', error);
      alert('Failed to reorder menus');
    }
  };

  const moveDown = async (menu, index) => {
    if (index === menus.length - 1) return;
    const nextMenu = menus[index + 1];
    
    try {
      await menusAPI.reorder([
        { id: menu.id, display_order: nextMenu.display_order },
        { id: nextMenu.id, display_order: menu.display_order }
      ]);
      await loadMenus();
    } catch (error) {
      console.error('Failed to reorder:', error);
      alert('Failed to reorder menus');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        color: '#666' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>📋</div>
          <p>Loading menus...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in-up">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px' 
      }}>
        <div>
          <h1 style={{ 
            fontSize: '36px', 
            fontWeight: '700', 
            marginBottom: '8px',
            color: '#1a1a1a' 
          }}>Menu Management</h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#666666',
            margin: 0 
          }}>Manage your widget menu items and ordering.</p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingMenu(null);
            setFormData({ title: '', icon: '', display_order: 0, is_active: true });
          }}
          style={{
            padding: '14px 24px',
            background: 'linear-gradient(135deg, #700000, #a30021)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(112,0,0,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(112,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(112,0,0,0.3)';
          }}
        >
          ➕ Add New Menu
        </button>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="glass-card" style={{ 
            maxWidth: '500px', 
            width: '90%' 
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              marginBottom: '20px', 
              color: '#1a1a1a' 
            }}>
              {editingMenu ? '✏️ Edit Menu' : '➕ Add New Menu'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#1a1a1a'
                }}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(112,0,0,0.2)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    background: 'rgba(255,255,255,0.5)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#1a1a1a'
                }}>Icon (Emoji)</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="🏠"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(112,0,0,0.2)',
                    borderRadius: '8px',
                    fontSize: '24px',
                    background: 'rgba(255,255,255,0.5)',
                    textAlign: 'center'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#1a1a1a'
                }}>Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(112,0,0,0.2)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    background: 'rgba(255,255,255,0.5)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer' 
                }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    style={{ 
                      marginRight: '8px', 
                      width: '18px', 
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '600', color: '#1a1a1a' }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingMenu(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'rgba(0,0,0,0.05)',
                    color: '#666',
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #700000, #a30021)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(112,0,0,0.3)'
                  }}
                >
                  {editingMenu ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="glass-card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(112,0,0,0.1)' }}>
              <th style={{ 
                padding: '16px', 
                textAlign: 'left', 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#1a1a1a', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px' 
              }}>Icon</th>
              <th style={{ 
                padding: '16px', 
                textAlign: 'left', 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#1a1a1a', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px' 
              }}>Title</th>
              <th style={{ 
                padding: '16px', 
                textAlign: 'center', 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#1a1a1a', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px' 
              }}>Order</th>
              <th style={{ 
                padding: '16px', 
                textAlign: 'left', 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#1a1a1a', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px' 
              }}>Status</th>
              <th style={{ 
                padding: '16px', 
                textAlign: 'right', 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#1a1a1a', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px' 
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {menus.map((menu, index) => (
              <tr key={menu.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <td style={{ padding: '16px', fontSize: '32px' }}>{menu.icon}</td>
                <td style={{ padding: '16px', fontWeight: '600', fontSize: '15px', color: '#1a1a1a' }}>
                  {menu.title}
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                    <button
                      onClick={() => moveUp(menu, index)}
                      disabled={index === 0}
                      style={{
                        padding: '6px 10px',
                        background: index === 0 ? 'rgba(0,0,0,0.05)' : 'rgba(112,0,0,0.1)',
                        color: index === 0 ? '#999' : '#700000',
                        border: index === 0 ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(112,0,0,0.2)',
                        borderRadius: '6px',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveDown(menu, index)}
                      disabled={index === menus.length - 1}
                      style={{
                        padding: '6px 10px',
                        background: index === menus.length - 1 ? 'rgba(0,0,0,0.05)' : 'rgba(112,0,0,0.1)',
                        color: index === menus.length - 1 ? '#999' : '#700000',
                        border: index === menus.length - 1 ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(112,0,0,0.2)',
                        borderRadius: '6px',
                        cursor: index === menus.length - 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ▼
                    </button>
                  </div>
                </td>
                <td style={{ padding: '16px' }}>
                  <button
                    onClick={() => toggleActive(menu)}
                    style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      background: menu.is_active 
                        ? 'linear-gradient(135deg, #059669, #10b981)' 
                        : 'linear-gradient(135deg, #dc2626, #ef4444)',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {menu.is_active ? '✓ Active' : '✕ Inactive'}
                  </button>
                </td>
                <td style={{ padding: '16px', textAlign: 'right' }}>
                  <button
                    onClick={() => handleEdit(menu)}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(112,0,0,0.1)',
                      color: '#700000',
                      border: '1px solid rgba(112,0,0,0.2)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginRight: '8px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(menu.id)}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(220,38,38,0.1)',
                      color: '#dc2626',
                      border: '1px solid rgba(220,38,38,0.2)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {menus.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            color: '#999' 
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No menus found</p>
            <p style={{ fontSize: '14px', color: '#bbb' }}>Click "Add New Menu" to create your first menu item</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MenuManagement;
